import { AccordionTypes, UI, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Pressable } from 'native-base';
import React, { useMemo, useState } from 'react';

import { ClosedMarketIndicator } from '../ClosedMarketIndicator';

import { getNoticeText } from './constants';

import { FiltersTypes } from '.';

const specificPdfDownloadExists = false;

export const FiltersMobile: React.FC<FiltersTypes.Props> = ({
  product,
  isExportingPdf,
  isToday,
  onDateSelected,
  onDownloadPdf,
  selectedDate,
  assetsFilterSelected,
  assetsFilters,
  assetsFiltersLoading,
  handleUpdateCustomerAssetsDetailsFiltersSelected,
}) => {
  const buttonDisabled = isExportingPdf || !specificPdfDownloadExists;

  const [accordionState, setAccordionState] = useState<AccordionTypes.Status>('closed');

  const handlePress = () => {
    setAccordionState(accordionState === 'closed' ? 'opened' : 'closed');
  };

  const handleClosed = () => {
    setAccordionState('closed');
  };

  const handleSelectDate = (date: Date) => {
    handleClosed();
    onDateSelected(date);
  };

  const handleSelectCategory = (index: number) => {
    handleClosed();
    handleUpdateCustomerAssetsDetailsFiltersSelected(index);
  };

  const handleDownloadPdf = () => {
    if (buttonDisabled) {
      return;
    }
    onDownloadPdf();
  };

  const noticeMemoized = useMemo(
    () => (
      <>
        {!!selectedDate && (
          <UI.Notice
            testId="notice-not-today"
            type="warning"
            backgroundColor={Utils.ColorToken.WithOpacity(
              'feedback.attention.lightest',
              'opacity.medium'
            )}
            textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
          />
        )}
      </>
    ),
    [selectedDate]
  );

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <ClosedMarketIndicator product={product} />

      <HStack justifyContent="space-between">
        <UI.Accordion
          testId="invested-assets-filter-accordion"
          status={accordionState}
          onPress={handlePress}
          onClosed={handleClosed}
          hasShadow={false}>
          <UI.Accordion.Visible
            hideDefaultIcons
            shadow="2"
            borderRadius="full"
            px={Spacing['inline.xxxs']}
            py={Spacing['inline.nano']}
            containerProps={{ p: 0 }}>
            <Pressable onPress={handlePress}>
              <HStack space={Spacing['inline.quarck']} alignItems="center">
                <UI.Text variant="semibold" fontSize={Typography['fontsize.md']}>
                  Filtrar
                </UI.Text>
                {!!selectedDate && (
                  <Box
                    rounded={'full'}
                    w={'20px'}
                    h={'20px'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    bg={'brand.tertiary.light'}>
                    <UI.Text
                      variant="regular"
                      fontSize={Typography['fontsize.xs']}
                      color={'neutral.white'}>
                      1
                    </UI.Text>
                  </Box>
                )}
              </HStack>
            </Pressable>
          </UI.Accordion.Visible>
          <UI.Accordion.Expanded>
            <VStack w="full" space={Spacing['stack.xxxs']}>
              <VStack w="full" space={Spacing['stack.nano']}>
                <UI.Text variant="semibold">Data da posição</UI.Text>
                <HStack maxW="full">
                  <UI.DatePicker
                    date={selectedDate}
                    onSelectDate={handleSelectDate}
                    onClear={() => onDateSelected(undefined)}
                    placeholder="Selecione a data da posição"
                  />
                </HStack>
              </VStack>
              <VStack w="full" space={Spacing['stack.nano']}>
                <UI.Text variant="semibold">Categorias</UI.Text>
                <UI.Tabs
                  testId="category-tabs"
                  isDisabled={assetsFiltersLoading}
                  isLoading={assetsFiltersLoading}
                  activeTab={assetsFilterSelected}
                  variant="secondary"
                  onTabPress={handleSelectCategory}
                  tabs={assetsFilters.map((filter) => ({
                    title: filter.label,
                  }))}
                />
              </VStack>
            </VStack>
          </UI.Accordion.Expanded>
        </UI.Accordion>
        <Pressable onPress={handleDownloadPdf}>
          <UI.Icon
            testID="export-pdf-button"
            name="Pdf"
            size="sm"
            color={Color[buttonDisabled ? 'neutral.medium' : 'brand.tertiary.principal']}
          />
        </Pressable>
      </HStack>
      {!isToday && !!selectedDate && noticeMemoized}
    </VStack>
  );
};
